import { useLayoutEffect, useEffect, useState, useMemo, useCallback, useRef, memo } from "react"
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

import useAppStore from "../../store/useAppStore"
import useLayoutStore from "../../store/useLayoutStore"

import LoadingSpinner from "../elements/LoadingSpinner";

const LOG_ROW_FLASH_MS = 520

const getKarnetDayValue = (dayData, interval) => {
    if (!dayData) return ""
    if (dayData[interval] != null) return dayData[interval]
    if (interval === "decimal" && dayData.formatted) {
        const [h, m] = dayData.formatted.split(":").map(Number)
        if (Number.isFinite(h) && Number.isFinite(m)) {
            return Math.round(((h * 60 + m) / 60) * 100) / 100
        }
    }
    return ""
}

const getRowClassName = (rowId, isFlashing, hoveredRowId) => {
    const classes = ["asv-emp-day-log-row"]
    if (isFlashing) classes.push("asv-emp-day-log-row--flash")
    if (String(rowId) === String(hoveredRowId)) classes.push("kv-row-hover")
    return classes.join(" ")
}

const formatKarnetTotal = (total) => {
    if (!Number.isFinite(total)) return ""
    return String(Math.round(total * 100) / 100)
}

const formatKarnetTotalAsTime = (total) => {
    if (!Number.isFinite(total) || total <= 0) return ""
    const totalMinutes = Math.round(total * 60)
    const h = Math.floor(totalMinutes / 60)
    const m = totalMinutes % 60
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
}

const KarnetFormView = () => {
    const { t } = useTranslation("karnet")
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)

    const activeThemeClass = isDarkTheme ? 'dark-color' : 'white-color'

    const karnetData = useAppStore(state => state.karnetData)
    const setKarnetData = useAppStore(state => state.setKarnetData)

    useLayoutEffect(() => {
        if (!karnetData) {
            setKarnetData(new Date().getMonth(), new Date().getFullYear())
        }
    }, [])

    return (
        <div className="view-container">
            <div className="view-header-container">
                <div className="view-header-top">
                    <div className={`v-header-name ${activeThemeClass}`}>{t("title")}</div>
                    <div className={`v-header-description ${isDarkTheme ? "dark-color" : ""}`}>{t("description")}</div>
                </div>
            </div>
            {/* initial={false} -> pri promeni pogleda ulaznu animaciju nosi samo
                InterfaceContentSwitcher; fade ostaje za prelaz spinner -> sadrzaj */}
            <AnimatePresence mode="wait" initial={false}>
                {karnetData === null ? (
                    <LoadingSpinner />
                ) : (
                    <motion.div
                        key="content"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.19 }}
                    >
                        <div className="kv-content-container">
                            <div className="kv-content-div">
                                <ViewKarnetToolbar />
                                <VIewKarnetTable />
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

export default KarnetFormView

const VIewKarnetTable = () => {
    const { t } = useTranslation("karnet")
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)

    const filteredKarnetData = useAppStore(state => state.filteredKarnetData)

    const karnetRoundingInterval = useAppStore(state => state.karnetRoundingInterval)

    const karnetFilter = useAppStore(state => state.karnetFilter)
    const loadingKarnetData = useAppStore(state => state.loadingKarnetData)
    const karnetRowFlashUserId = useAppStore(state => state.karnetRowFlashUserId)
    const karnetRowFlashTick = useAppStore(state => state.karnetRowFlashTick)

    const [tooltip, setTooltip] = useState({ show: false, content: "", x: 0, y: 0 })

    const currentCellRef = useRef(null)
    const monthKey = `${karnetFilter.year}-${karnetFilter.month}`
    const [flashingRowId, setFlashingRowId] = useState(null)
    const [hoveredRowId, setHoveredRowId] = useState(null)

    useEffect(() => {
        setFlashingRowId(null)
    }, [monthKey, karnetRoundingInterval])

    useEffect(() => {
        if (!karnetRowFlashUserId) return undefined
        setFlashingRowId(String(karnetRowFlashUserId))
        const timer = window.setTimeout(() => setFlashingRowId(null), LOG_ROW_FLASH_MS)
        return () => window.clearTimeout(timer)
    }, [karnetRowFlashUserId, karnetRowFlashTick])

    const headers = useMemo(() => {
        if (!filteredKarnetData || loadingKarnetData) return []
        const { month, year } = karnetFilter
        const daysInMonth = new Date(year, month + 1, 0).getDate()
        return [t("columns.number"), t("columns.firstName"), t("columns.lastName"), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)]
    }, [filteredKarnetData, loadingKarnetData, karnetFilter, t])

    const rows = useMemo(() => {
        if (!filteredKarnetData || loadingKarnetData) {
            return []
        }

        const { month, year } = karnetFilter
        const daysInMonth = new Date(year, month + 1, 0).getDate()

        return Object.entries(filteredKarnetData).map(([id, user]) => {
            let total = 0

            const days = Array.from({ length: daysInMonth }, (_, i) => {
                const day = (i + 1).toString()
                const value = user.days[day]

                if (!value) return { display: "", formatted: "" }

                const display = getKarnetDayValue(value, karnetRoundingInterval)

                if (Number.isFinite(display)) total += display

                return {
                    display,
                    formatted: value.formatted
                }
            })

            return {
                id,
                ime: user.ime,
                prezime: user.prezime,
                days,
                total: formatKarnetTotal(total),
                totalFormatted: formatKarnetTotalAsTime(total),
            }
        }).sort((a, b) => {
            const aName = `${a?.ime || ""} ${a?.prezime || ""}`.trim()
            const bName = `${b?.ime || ""} ${b?.prezime || ""}`.trim()
            return aName.localeCompare(bName, "sr", { sensitivity: "base" })
        })
    }, [filteredKarnetData, karnetRoundingInterval, loadingKarnetData, karnetFilter])

    const showTooltip = useCallback((e, content, cellId) => {
        currentCellRef.current = cellId

        setTooltip({
            show: true,
            content,
            x: e.clientX,
            y: e.clientY
        })
    }, [])


    const hideTooltip = useCallback((cellId) => {
        if (currentCellRef.current === cellId) {
            setTooltip(prev => ({ ...prev, show: false }))
            currentCellRef.current = null
        }
    }, [])

    const handleCellEnter = useCallback((e, dayIndex, formatted) => {
        const header = document.querySelector(`.kv-main-table thead th:nth-child(${dayIndex + 4})`)
        header?.classList.add("hover-col")
        showTooltip(e, formatted)
    }, [showTooltip])

    const handleCellLeave = useCallback((dayIndex) => {
        const header = document.querySelector(`.kv-main-table thead th:nth-child(${dayIndex + 4})`)
        header?.classList.remove("hover-col")
        hideTooltip()
    }, [hideTooltip])

    const handleTotalEnter = useCallback((e, totalFormatted) => {
        if (!totalFormatted) return
        showTooltip(e, totalFormatted)
    }, [showTooltip])

    const handleTotalLeave = useCallback(() => {
        hideTooltip()
    }, [hideTooltip])

    /* Hover mora da se deli izmedju skrol tabele i fiksne kolone "Ukupno",
       jer CSS :hover ne moze da prelazi granicu izmedju dve tabele */
    const getRowProps = useCallback((rowId) => ({
        onMouseEnter: () => setHoveredRowId(rowId),
        onMouseLeave: () => setHoveredRowId(null)
    }), [])

    /* Stagger celija je CSS animacija; faza se smenjuje "a" <-> "b" da bi se animacija
       restartovala i kad se promeni zaokruzivanje (tbody ostaje mount-ovan) i kad se promeni
       mesec/godina (tbody se remount-uje pa animacija krece sa novom fazom).
       Talas igra i pri otvaranju karneta, ali tada sa dodatnim delay-om (data-kv-swap-enter)
       da se ne preklapa sa ulaznom animacijom celog pogleda. */
    const cellSwapRef = useRef({
        monthKey,
        interval: karnetRoundingInterval,
        phase: "a",
        isViewEnter: true
    })

    if (cellSwapRef.current.monthKey !== monthKey || cellSwapRef.current.interval !== karnetRoundingInterval) {
        cellSwapRef.current = {
            monthKey,
            interval: karnetRoundingInterval,
            phase: cellSwapRef.current.phase === "a" ? "b" : "a",
            isViewEnter: false
        }
    }

    const cellSwapPhase = cellSwapRef.current.phase
    const cellSwapEnter = cellSwapRef.current.isViewEnter ? "" : undefined

    return (
        <AnimatePresence mode="wait" initial={false}>
            {loadingKarnetData ? (
                <LoadingSpinner />
            ) : (
                <motion.div
                    key={monthKey}
                    className="kv-table-container kv-table-shell"
                    initial={{ opacity: 0.74 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.19, ease: "easeOut" }}
                >
                    <div className="kv-table-scroll">
                        <table className={`kv-table kv-main-table ${isDarkTheme ? 'dark-table' : ''} `}>
                            <thead>
                                <tr>
                                    {headers.map((h, idx) => (
                                        <th key={idx} className={idx === 0 ? "st-row-num-col" : undefined}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody data-kv-swap={cellSwapPhase || undefined} data-kv-swap-enter={cellSwapEnter}>
                                {rows.map((row, rowIdx) => {
                                    const isFlashing = String(row.id) === String(flashingRowId)

                                    return (
                                        <tr
                                            key={row.id}
                                            className={getRowClassName(row.id, isFlashing, hoveredRowId)}
                                            style={{ "--kv-row": String(rowIdx) }}
                                            {...getRowProps(row.id)}
                                        >
                                            <td className="st-row-num-col">{rowIdx + 1}</td>
                                            <td>{row.ime}</td>
                                            <td>{row.prezime}</td>
                                            {row.days.map((dayData, idx) => (
                                                <KarnetDayCell
                                                    key={idx}
                                                    dayIndex={idx}
                                                    display={dayData.display}
                                                    formatted={dayData.formatted}
                                                    onCellEnter={handleCellEnter}
                                                    onCellLeave={handleCellLeave}
                                                />
                                            ))}
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className="kv-table-totals">
                        <table className={`kv-table kv-totals-table ${isDarkTheme ? 'dark-table' : ''} `}>
                            <thead>
                                <tr>
                                    <th className="kv-total-col">{t("columns.total")}</th>
                                </tr>
                            </thead>
                            <tbody data-kv-swap={cellSwapPhase || undefined} data-kv-swap-enter={cellSwapEnter}>
                                {rows.map((row, rowIdx) => {
                                    const isFlashing = String(row.id) === String(flashingRowId)

                                    return (
                                        <tr
                                            key={row.id}
                                            className={getRowClassName(row.id, isFlashing, hoveredRowId)}
                                            style={{ "--kv-row": String(rowIdx) }}
                                            {...getRowProps(row.id)}
                                        >
                                            <td
                                                className="kv-total-col"
                                                style={{ "--kv-col": String(row.days.length) }}
                                                onMouseEnter={(e) => handleTotalEnter(e, row.totalFormatted)}
                                                onMouseLeave={handleTotalLeave}
                                            >
                                                {row.total !== "" ? (
                                                    <span className="kv-table-cell-value">{row.total}</span>
                                                ) : null}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                    <AnimatePresence mode="wait">
                        {tooltip.show && (
                            <motion.div
                                key={currentCellRef.current}
                                className={`kv-table-custom-tooltip ${isDarkTheme ? 'dark-table-tooltip' : ''}`}
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.8 }}
                                transition={{ duration: 0.14, ease: "easeOut" }}
                                style={{
                                    position: 'fixed',
                                    left: tooltip.x + 14,
                                    top: tooltip.y + 19,
                                    background: '#afafafff',
                                    color: '#fff',
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    zIndex: 1000,
                                    pointerEvents: 'none',
                                    transformOrigin: 'top left'
                                }}
                            >
                                {tooltip.content}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>
            )}
        </AnimatePresence>
    )
}


const KarnetDayCell = memo(({ dayIndex, display, formatted, onCellEnter, onCellLeave }) => {
    const hasValue = display !== "" && display != null

    return (
        <td
            style={{ "--kv-col": String(dayIndex) }}
            onMouseEnter={(e) => onCellEnter(e, dayIndex, formatted)}
            onMouseLeave={() => onCellLeave(dayIndex)}
        >
            {hasValue ? <span className="kv-table-cell-value">{display}</span> : null}
        </td>
    )
})

const ViewKarnetToolbar = () => {
    const { t } = useTranslation("karnet")
    const karnetFilter = useAppStore(state => state.karnetFilter)
    const setKarnetData = useAppStore(state => state.setKarnetData)

    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)
    const months = t("months", { returnObjects: true })

    const handleMonthChange = (month) => {
        setKarnetData(month, karnetFilter.year);
        setIsMonthOpen(false);
    };

    const handleYearChange = (year) => {
        setKarnetData(karnetFilter.month, year);
        setIsYearOpen(false);
    };

    const [isMonthOpen, setIsMonthOpen] = useState(false);
    const [isYearOpen, setIsYearOpen] = useState(false);


    return (
        <div className="kv-toolbar-container">
            <div className="kv-filters">
                <div className="kv-filter-item">
                    <label className={`${isDarkTheme ? 'dark-color' : ''}`}>{t("filters.month")}</label>
                    <div
                        className={`kv-dropdown ${isDarkTheme ? 'dark-dropdown' : ''}`}
                        onClick={() => setIsMonthOpen(!isMonthOpen)}
                    >
                        {months[karnetFilter.month]}
                        <motion.div
                            className="arrow"
                        >
                            <svg
                                viewBox="0 0 24 24"
                                width="24"
                                height="24"
                                style={{ display: "block" }}
                            >
                                <path
                                    d="M7 10l5 5 5-5"
                                    fill="none"
                                    stroke={isDarkTheme ? "rgb(219, 219, 219)" : "black"}
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </motion.div>
                        <AnimatePresence>
                            {isMonthOpen && (
                                <motion.div
                                    className={`kv-dropdown-list ${isDarkTheme ? 'dark-dropdownlist' : ''}`}
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.14, ease: "easeOut" }}
                                >
                                    {months.map((m, idx) => (
                                        <div
                                            key={idx}
                                            className={`kv-dropdown-item ${isDarkTheme ? 'dark-dropdownitem' : ''}`}
                                            onClick={() => handleMonthChange(idx)}
                                        >
                                            {m}
                                        </div>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>

                <div className="kv-filter-item">
                    <label className={`${isDarkTheme ? 'dark-color' : ''}`}>{t("filters.year")}</label>
                    <div
                        className={`kv-dropdown ${isDarkTheme ? 'dark-dropdown' : ''}`}
                        onClick={() => setIsYearOpen(!isYearOpen)}
                    >
                        {karnetFilter.year}
                        <motion.div
                            className="arrow"
                        >
                            <svg
                                viewBox="0 0 24 24"
                                width="24"
                                height="24"
                                style={{ display: "block" }}
                            >
                                <path
                                    d="M7 10l5 5 5-5"
                                    fill="none"
                                    stroke={isDarkTheme ? "rgb(219, 219, 219)" : "black"}
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </motion.div>
                        <AnimatePresence>
                            {isYearOpen && (
                                <motion.div
                                    className={`kv-dropdown-list ${isDarkTheme ? 'dark-dropdownlist' : ''}`}
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.14, ease: "easeOut" }}
                                >
                                    {Array.from({ length: 6 }, (_, i) => {
                                        const y = new Date().getFullYear() - i;
                                        return (
                                            <div
                                                key={y}
                                                className={`kv-dropdown-item ${isDarkTheme ? 'dark-dropdownitem' : ''}`}
                                                onClick={() => handleYearChange(y)}
                                            >
                                                {y}
                                            </div>
                                        );
                                    })}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </div>
            <KarnetToolbarActions />
        </div>
    );
};

const KarnetToolbarActions = () => {
    const { t } = useTranslation("karnet")
    const karnetFilter = useAppStore(state => state.karnetFilter)
    const karnetRoundingInterval = useAppStore(state => state.karnetRoundingInterval)
    const setKarnetRoundingInterval = useAppStore(state => state.setKarnetRoundingInterval)

    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)
    const months = t("months", { returnObjects: true })

    const handleExportExcel = useCallback(() => {
        const { filteredKarnetData } = useAppStore.getState();
        if (!filteredKarnetData) return;

        const { month, year } = karnetFilter;
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const headers = [t("columns.firstName"), t("columns.lastName"), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

        const rows = Object.entries(filteredKarnetData)
            .map(([id, user]) => ({
                ime: user.ime || "",
                prezime: user.prezime || "",
                days: Array.from({ length: daysInMonth }, (_, i) => {
                    const day = (i + 1).toString();
                    const dayData = user.days[day];

                    if (!dayData) return "";

                    return getKarnetDayValue(dayData, karnetRoundingInterval);
                }),
            }))
            .sort((a, b) => {
                const aName = `${a.ime} ${a.prezime}`.trim();
                const bName = `${b.ime} ${b.prezime}`.trim();
                return aName.localeCompare(bName, "sr", { sensitivity: "base" });
            })
            .map((user) => [user.ime, user.prezime, ...user.days]);

        const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, t("sheetName"));

        const fileName = `karnet_${months[month]}_${year}.xlsx`;
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), fileName);
    }, [karnetFilter, karnetRoundingInterval, months, t]);

    const handleChangeInterval = (e) => {
        const target = e.target.closest('[data-name]');
        if (!target) return;
        const name = target.getAttribute('data-name');
        setKarnetRoundingInterval(name)
    }

    return (
        <div className="kv-tools-container">
            <div className="kv-tools-div" onClick={handleChangeInterval}>
                <KarnetHeaderButton
                    name="round30min"
                    icon="./views/30min.svg"
                    active={karnetRoundingInterval === 'round30min'}
                    tooltip={t("round30min")}
                />
                <KarnetHeaderButton
                    name="round1h"
                    icon={"./views/1h.svg"}
                    active={karnetRoundingInterval === 'round1h'}
                    tooltip={t("round1h")}
                />
                <KarnetHeaderButton
                    name="decimal"
                    icon="./views/decimal.svg"
                    active={karnetRoundingInterval === 'decimal'}
                    tooltip={t("decimal")}
                />
            </div>
            <button className={`kv-xlsx-button ${isDarkTheme ? 'dark-big-btn ' : ''}`} onClick={handleExportExcel}>
                {t("generateXlsx")}
            </button>
        </div>
    )
}

const KarnetHeaderButton = ({ name, active, icon, tooltip }) => {
    const isTouchDevice = useLayoutStore(state => state.isTouchDevice)
    const [isHovered, setIsHovered] = useState(false);

    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)
    const activeClass = isDarkTheme ? 'dark-active' : 'active'

    return (
        <div className="v-header-button-div">
            <button
                className={`v-header-icon-button ${active ? activeClass : ""}`}
                data-name={name}
                onMouseEnter={() => !isTouchDevice && setIsHovered(true)}
                onMouseLeave={() => !isTouchDevice && setIsHovered(false)}
            >
                <img src={icon} alt={name} className={`${isDarkTheme ? 'dark-filter' : ''}`} />
            </button>
            <AnimatePresence>
                {isHovered && (
                    <motion.div
                        className={`v-header-button-tooltip  ${isDarkTheme ? 'tooltip-dark' : 'tooltip-white'}`}
                        initial={{ opacity: 0, y: -14 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -14 }}
                        transition={{ duration: 0.19, ease: "easeOut" }}
                    >
                        {tooltip}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
